import { Timing } from "./timing";
import type { BattleEvent } from "./events";
import type { Side } from "./operation";
import { BattleRandom } from "./random";
import { Pet, BattleStatus } from "./pet";
import { createDefaultProps, Prop } from "./prop";

/**
 * Seer：封装本模块的领域数据与相关操作。
 */
export class Seer {
    /**
     * random，保存当前实例的random状态。
     */
    random = new BattleRandom();

    /**
     * 出战精灵背包
     */
    petPackage: Pet[];
    /**
     * 本场战斗可用道具
     */
    propPackage: Prop[];
    /**
     * 最近阵亡的精灵序号（随局面快照克隆，用于阵亡后界面仍显示该精灵）
     */
    lastDeadPetId?: number = undefined;
    /**
     * 本回合是否先出手
     * @description true=先出手，false=后出手，undefined=本轮尚未排序；由 Round.compute() 在战斗阶段开始后填充
     */
    先出手?: boolean = undefined;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param petPackage petPackage参数，参与本次操作；默认值为 `[]`
     */
    constructor(petPackage: Pet[] = []) {
        this.petPackage = petPackage;
        this.propPackage = createDefaultProps();
        if (this.petPackage.length > 0) {
            this.petPackage[0].战斗状态 = BattleStatus.FIGHTING;
        }
    }

    /**
     * 标记精灵阵亡并记录为最近阵亡
     */
    markPetDead(pet: Pet) {
        pet.战斗状态 = BattleStatus.DEAD;
        this.lastDeadPetId = pet.序号;
    }

    /** 领域入口只接受本方合法存活替补，先校验再改变当前精灵。 */
    changeBattlePet(
        petId: number,
        objSeer: Seer,
        details: { /**
         * side，保存当前实例的side状态。
         */
        side?: Side; /**
         * events，保存当前实例的events状态。
         */
        events?: BattleEvent[] } = {},
    ): boolean {
        /**
         * incoming，供本模块相关规则使用。
         */
        const incoming = this.petPackage.find((pet) => pet.序号 === petId);
        if (!incoming || incoming.战斗状态 !== BattleStatus.STANDBY || !incoming.isAlive())
            return false;
        /**
         * outgoing，供本模块相关规则使用。
         */
        const outgoing = this.getActivePet();
        /**
         * opponent，供本模块相关规则使用。
         */
        const opponent = objSeer.getBattlePet();
        /**
         * dispatch，供本模块相关规则使用。
         */
        const dispatch = (pet: Pet, timing: Timing) => {
            details.events?.push({ timing, side: details.side });
            if (opponent)
                pet.runGlobalTiming(timing, {
                    sbjPet: pet,
                    objPet: opponent,
                    sbjSeer: this,
                    objSeer,
                    timing,
                    ...details,
                });
        };
        if (outgoing) {
            dispatch(outgoing, Timing.petOff);
            outgoing.leaveBattle();
        }
        incoming.enterBattle();
        dispatch(incoming, Timing.petOn);
        return true;
    }

    /** 结算只能使用真正出战精灵；getBattlePet 另外提供界面残局展示。 */
    getActivePet(): Pet | undefined {
        return this.petPackage.find((pet) => pet.战斗状态 === BattleStatus.FIGHTING);
    }

    /**
     * 获取当前出战精灵
     * @returns 当前处于出战状态的精灵；若无出战精灵（阵亡未切换）则返回最近阵亡的精灵，最终兜底返回背包首只；空背包返回 undefined
     */
    getBattlePet(): Pet | undefined {
        /**
         * battlePet，供本模块相关规则使用。
         */
        const battlePet = this.petPackage.find((p) => p.战斗状态 === BattleStatus.FIGHTING) as
            Pet | undefined;
        if (battlePet) {
            return battlePet;
        }
        // 无出战精灵时返回最近阵亡的精灵，便于界面显示残局
        /**
         * deadPets，供本模块相关规则使用。
         */
        const deadPets = this.petPackage.filter((p) => p.战斗状态 === BattleStatus.DEAD);
        if (deadPets.length > 0) {
            /**
             * deadPet，供本模块相关规则使用。
             */
            const deadPet = (deadPets.find((p) => p.序号 === this.lastDeadPetId) ??
                deadPets[0]) as Pet;
            return deadPet;
        }
        // 兜底：返回背包首只（避免界面取不到精灵）
        return this.petPackage[0];
    }
}
